import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, Typography, Paper, CircularProgress, IconButton, Tooltip, LinearProgress, Chip, Switch, FormControlLabel } from '@mui/material';
import axios from 'axios';
import ImageFloorPlanHeatmap, { countUsersPerZone } from './ImageFloorPlanHeatmap';
import { getFloorPlanConfig } from './floorPlanConfigs';

// API Base URL from environment
const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/simulation/live/data/`;

// Sliding window size for machine visit tracking (keeps last N polls)
const VISIT_WINDOW_SIZE = 30;

/**
 * TL Individual Live Feed Component
 * 
 * Improvements:
 * - Machine matching by name + ID (handles backend ID mismatch)
 * - Sliding-window machine visits (decays over time instead of ever-growing)
 * - Proper zone alignment with backend simulation zones
 * - Toggle controls for heatmap / users / machines / zone labels
 * - Better stats display: zone breakdown, machine utilization, occupancy bar
 * - Shows all machines (active + idle) in side panel
 */
const TLIndividualLiveFeed = ({ siteId = 2 }) => {
    // Site-specific floor plan config (zones + machines)
    const floorConfig = React.useMemo(() => getFloorPlanConfig(siteId), [siteId]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Machine visit tracking: sliding window of active machine IDs
    const visitWindowRef = useRef([]); // array of Sets, each Set = active machine IDs from one poll
    const [machineVisits, setMachineVisits] = useState({});

    // Toggle controls
    const [showHeatmap, setShowHeatmap] = useState(true);
    const [showUsers, setShowUsers] = useState(true);
    const [showMachines, setShowMachines] = useState(true);
    const [showZoneLabels, setShowZoneLabels] = useState(true);
    const [showDensityHeatmap, setShowDensityHeatmap] = useState(true);

    // Machine visit sliding-window: match against active site's FLOOR_PLAN_MACHINES
    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const response = await axios.get(`${API_BASE}?site_id=${siteId}`);
                if (cancelled) return;

                // The backend wraps every response in a {Result, Message, Code, Data}
                // envelope (custom DRF renderer) - unwrap it, falling back to the raw
                // body for any endpoint that isn't wrapped.
                const payload = response.data?.Data ?? response.data;

                setData(payload);
                setLoading(false);
                setError(null);

                if (payload?.live_machines) {
                    const activeSet = new Set();
                    payload.live_machines.forEach(m => {
                        if (m.status === 'active') {
                            activeSet.add(m.id);
                            if (m.name) activeSet.add(m.name);
                        }
                    });

                    // Cross-match against this site's floor plan machines
                    payload.live_machines.forEach(apiMachine => {
                        if (apiMachine.status !== 'active') return;
                        Object.values(floorConfig.machines).forEach(fp => {
                            const apiNorm = (apiMachine.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                            const fpNorm = fp.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                            if (apiMachine.id === fp.id || apiNorm === fpNorm || (apiMachine.name || '').toLowerCase().includes(fp.name.toLowerCase())) {
                                activeSet.add(fp.id);
                            }
                        });
                    });

                    visitWindowRef.current.push(activeSet);
                    if (visitWindowRef.current.length > VISIT_WINDOW_SIZE) {
                        visitWindowRef.current.shift();
                    }

                    const counts = {};
                    visitWindowRef.current.forEach(s => {
                        s.forEach(id => {
                            if (typeof id === 'number') {
                                counts[id] = (counts[id] || 0) + 1;
                            }
                        });
                    });
                    setMachineVisits(counts);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching live simulation data:', err);
                    setError(err.message || 'Failed to fetch live data');
                }
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 2000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [siteId, floorConfig]);

    // Zone user counts (computed from live user positions)
    const zoneUserCounts = React.useMemo(
        () => countUsersPerZone(data?.live_users || [], floorConfig.zoneBounds),
        [data?.live_users, floorConfig]
    );

    // Machine utilization stats
    const machineStats = React.useMemo(() => {
        const machines = data?.live_machines || [];
        const total = machines.length;
        const active = machines.filter(m => m.status === 'active').length;
        const avgLoad = active > 0
            ? Math.round(machines.filter(m => m.status === 'active').reduce((s, m) => s + (m.load || 0), 0) / active)
            : 0;
        return { total, active, idle: total - active, avgLoad };
    }, [data?.live_machines]);

    // Peak zone (zone with most users)
    const peakZone = React.useMemo(() => {
        let maxKey = null, maxCount = 0;
        Object.entries(zoneUserCounts).forEach(([k, v]) => {
            if (v > maxCount) { maxCount = v; maxKey = k; }
        });
        return maxKey ? { key: maxKey, count: maxCount, label: floorConfig.zoneBounds[maxKey]?.label || maxKey } : null;
    }, [zoneUserCounts, floorConfig]);

    // Helper to get friendly machine name (Prioritize local map over API dummy names)
    const getMachineName = useCallback((apiMachine) => {
        for (const key in floorConfig.machines) {
            if (String(floorConfig.machines[key].id) === String(apiMachine.id)) {
                return floorConfig.machines[key].name;
            }
        }
        return apiMachine.name || `Machine #${apiMachine.id}`;
    }, [floorConfig]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 10, gap: 2 }}>
                <CircularProgress size={40} />
                <Typography sx={{ color: '#888', fontSize: '14px' }}>Loading live data for Site {siteId}...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 10, gap: 2 }}>
                <Typography sx={{ color: '#d32f2f', fontWeight: 600 }}>Connection Error</Typography>
                <Typography sx={{ color: '#888', fontSize: '13px' }}>{error}</Typography>
                <Typography sx={{ color: '#999', fontSize: '12px' }}>Retrying automatically...</Typography>
            </Box>
        );
    }

    const totalUsers = data?.live_users?.length || 0;
    const maxCap = data?.max_capacity || 20;
    const occupancyPct = Math.round((totalUsers / maxCap) * 100);

    return (
        <Box>
            {/* Header with stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 10, height: 10, borderRadius: '50%', bgcolor: '#f44336',
                        animation: 'headerPulse 1.5s infinite',
                        '@keyframes headerPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                    }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                        {data?.site_name || `Site ${siteId}`} — Live Feed
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Occupancy */}
                    <Box sx={{ bgcolor: '#e3f2fd', px: 2, py: 0.8, borderRadius: '10px', border: '1px solid #bbdefb', minWidth: 120 }}>
                        <Typography sx={{ color: '#1565c0', fontWeight: 700, fontSize: '14px' }}>
                            {totalUsers} / {maxCap} Occupancy
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={occupancyPct}
                            sx={{
                                mt: 0.5, height: 4, borderRadius: 2,
                                bgcolor: '#bbdefb',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: occupancyPct > 80 ? '#f44336' : occupancyPct > 50 ? '#ff9800' : '#1e88e5',
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>

                    {/* Active Machines */}
                    <Box sx={{ bgcolor: '#e8f5e9', px: 2, py: 0.8, borderRadius: '10px', border: '1px solid #c8e6c9' }}>
                        <Typography sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '14px' }}>
                            {machineStats.active} / {machineStats.total} Machines
                        </Typography>
                        <Typography sx={{ color: '#66bb6a', fontSize: '11px' }}>
                            Avg Load: {machineStats.avgLoad}%
                        </Typography>
                    </Box>

                    {/* Peak Zone */}
                    {peakZone && peakZone.count > 0 && (
                        <Box sx={{ bgcolor: '#fff3e0', px: 2, py: 0.8, borderRadius: '10px', border: '1px solid #ffe0b2' }}>
                            <Typography sx={{ color: '#e65100', fontWeight: 700, fontSize: '14px' }}>
                                Peak: {peakZone.label}
                            </Typography>
                            <Typography sx={{ color: '#ff9800', fontSize: '11px' }}>
                                {peakZone.count} people
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Toggle Controls */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#888', fontWeight: 600, mr: 1 }}>Layers:</Typography>
                {[
                    { label: 'Density Heat', val: showDensityHeatmap, set: setShowDensityHeatmap },
                    { label: 'Zone Heat', val: showHeatmap, set: setShowHeatmap },
                    { label: 'Users', val: showUsers, set: setShowUsers },
                    { label: 'Machines', val: showMachines, set: setShowMachines },
                    { label: 'Zone Labels', val: showZoneLabels, set: setShowZoneLabels },
                ].map(ctrl => (
                    <Chip
                        key={ctrl.label}
                        label={ctrl.label}
                        size="small"
                        variant={ctrl.val ? 'filled' : 'outlined'}
                        color={ctrl.val ? 'primary' : 'default'}
                        onClick={() => ctrl.set(!ctrl.val)}
                        sx={{
                            fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            transition: 'all 0.2s ease',
                        }}
                    />
                ))}
            </Box>

            <Grid container spacing={3}>
                {/* Main Floor Plan */}
                <Grid item xs={12} lg={10}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '20px',
                            border: '1px solid #e6e6e6',
                            bgcolor: '#fff',
                            overflow: 'hidden',
                        }}
                    >
                        <ImageFloorPlanHeatmap
                            liveUsers={data?.live_users || []}
                            liveMachines={data?.live_machines || []}
                            machineVisits={machineVisits}
                            showHeatmap={showHeatmap}
                            showMachines={showMachines}
                            showUsers={showUsers}
                            showZoneLabels={showZoneLabels}
                            showDensityHeatmap={showDensityHeatmap}
                            floorPlanImage={floorConfig.image}
                            floorPlanMachines={floorConfig.machines}
                            floorPlanZoneBounds={floorConfig.zoneBounds}
                            onMachineClick={(key, machine) => {
                                console.log('Machine clicked:', key, machine);
                            }}
                            onZoneClick={(key, zone) => {
                                console.log('Zone clicked:', key, zone);
                            }}
                        />
                    </Paper>
                </Grid>

                {/* Side Panel: Zones + Machines */}
                <Grid item xs={12} lg={2}>
                    <Paper
                        elevation={0}
                        sx={{ p: 1.5, borderRadius: '20px', border: '1px solid #e6e6e6', height: '100%' }}
                    >
                        {/* Zone Activity */}
                        <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '13px', color: '#333' }}>
                            Zone Activity
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 2 }}>
                            {Object.entries(floorConfig.zoneBounds)
                                .filter(([k]) => !['STORAGE', 'ENTRANCE'].includes(k))
                                .map(([zoneKey, zone]) => {
                                    const count = zoneUserCounts[zoneKey] || 0;
                                    const heat = count > 0 ? Math.min(0.15 + count * 0.22, 1) : 0;
                                    const isHot = heat > 0.5;

                                    return (
                                        <Box key={zoneKey} sx={{
                                            p: 0.8, borderRadius: '8px',
                                            bgcolor: count > 0 ? zone.color : '#fafafa',
                                            border: `1px solid ${count > 0 ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.03)'}`,
                                            transition: 'all 0.3s ease',
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', fontSize: '10px' }}>
                                                    {zone.icon} {zone.label}
                                                </Typography>
                                                {isHot && (
                                                    <Chip
                                                        label="HOT"
                                                        size="small"
                                                        sx={{
                                                            height: 16, fontSize: '8px', fontWeight: 800,
                                                            bgcolor: '#ff5722', color: '#fff',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.2 }}>
                                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: count > 0 ? '#333' : '#aaa' }}>
                                                    {count} {count === 1 ? 'Person' : 'People'}
                                                </Typography>
                                                {count > 0 && (
                                                    <Box sx={{
                                                        width: 40, height: 4, borderRadius: 2, bgcolor: '#e0e0e0',
                                                        overflow: 'hidden',
                                                    }}>
                                                        <Box sx={{
                                                            width: `${Math.min(count * 25, 100)}%`,
                                                            height: '100%',
                                                            bgcolor: heat > 0.6 ? '#f44336' : heat > 0.3 ? '#ff9800' : '#4caf50',
                                                            borderRadius: 2,
                                                            transition: 'width 0.5s ease',
                                                        }} />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })}
                        </Box>

                        {/* Machine List */}
                        <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '13px', color: '#333' }}>
                            Machines ({machineStats.active}/{machineStats.total})
                        </Typography>

                        {/* Active Machines */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, maxHeight: '250px', overflowY: 'auto' }}>
                            {(data?.live_machines || [])
                                .sort((a, b) => {
                                    // Active first, then by load descending
                                    if (a.status === 'active' && b.status !== 'active') return -1;
                                    if (a.status !== 'active' && b.status === 'active') return 1;
                                    return (b.load || 0) - (a.load || 0);
                                })
                                .map(machine => {
                                    const isActive = machine.status === 'active';
                                    return (
                                        <Box
                                            key={machine.id}
                                            sx={{
                                                p: 0.8, borderRadius: '6px',
                                                bgcolor: isActive ? '#f0fff4' : '#fafafa',
                                                border: `1px solid ${isActive ? '#c8e6c9' : '#eee'}`,
                                                display: 'flex', alignItems: 'center', gap: 0.8,
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            <Box sx={{
                                                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                                bgcolor: isActive ? '#4caf50' : '#bdbdbd',
                                            }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontSize: '11px', fontWeight: 600,
                                                    color: isActive ? '#333' : '#999',
                                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                }}>
                                                    {getMachineName(machine)}
                                                </Typography>
                                                {isActive && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                                        <Box sx={{
                                                            flex: 1, height: 3, borderRadius: 2, bgcolor: '#e0e0e0',
                                                            overflow: 'hidden',
                                                        }}>
                                                            <Box sx={{
                                                                width: `${machine.load}%`, height: '100%',
                                                                bgcolor: machine.load > 80 ? '#f44336' : machine.load > 50 ? '#ff9800' : '#4caf50',
                                                                borderRadius: 2, transition: 'width 0.5s ease',
                                                            }} />
                                                        </Box>
                                                        <Typography sx={{ fontSize: '9px', color: '#666', fontWeight: 600, minWidth: 28 }}>
                                                            {machine.load}%
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })
                            }
                            {(!data?.live_machines || data.live_machines.length === 0) && (
                                <Typography sx={{ fontSize: '12px', color: '#999', textAlign: 'center', py: 2 }}>
                                    No machine data available
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TLIndividualLiveFeed;
